import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedPage from '../components/shared/AnimatedPage';
import AppFooter from '../components/shared/AppFooter';
import { useAuthStore } from '../stores/authStore';
import { updateProfile, completeSignUp } from '../lib/functions';
import { useDarkStore } from '../app/store/darkStore';
import { moderateText } from '../utils/contentModeration';

type ToneOption = 'cheerful' | 'chill' | 'curious' | 'coach';

interface ToneCard {
  tone: ToneOption;
  label: string;
  sample: string;
  emoji: string;
}

const TONE_OPTIONS: ToneCard[] = [
  { tone: 'cheerful', label: 'Cheerful', sample: "Let's goooo! 🎉", emoji: '🎉' },
  { tone: 'chill', label: 'Chill', sample: 'No worries, we got this 😎', emoji: '😎' },
  { tone: 'curious', label: 'Curious', sample: 'Hmm, interesting... 🤔', emoji: '🤔' },
  { tone: 'coach', label: 'Coach', sample: "Let's level up! 💪", emoji: '💪' },
];

interface AvatarDef {
  id: string;
  name: string;
  color: string;
  bg: string;
  border: string;
  svg: () => ReactElement;
}

const face = (fx: number, fy: number, bodyColor: string) => (
  <>
    <circle cx={fx - 11} cy={fy} r={6} fill="#1a1a2e" />
    <circle cx={fx - 9} cy={fy - 2} r={2.5} fill="white" />
    <circle cx={fx + 11} cy={fy} r={6} fill="#1a1a2e" />
    <circle cx={fx + 13} cy={fy - 2} r={2.5} fill="white" />
    <path d={`M${fx - 12} ${fy + 13} Q${fx} ${fy + 21} ${fx + 12} ${fy + 13}`} stroke="#1a1a2e" strokeWidth="3.5" fill="none" strokeLinecap="round" />
    {/* blush */}
    <ellipse cx={fx - 16} cy={fy + 8} rx={5} ry={3} fill={bodyColor} opacity={0.55} />
    <ellipse cx={fx + 16} cy={fy + 8} rx={5} ry={3} fill={bodyColor} opacity={0.55} />
  </>
);

const arm = (x: number, y: number, color: string) => (
  <rect x={x} y={y} width="15" height="10" rx="5" fill={color} />
);

const AVATARS: AvatarDef[] = [
  {
    id: 'boxy', name: 'Boxy', color: '#4A9EFF', bg: 'rgba(74,158,255,0.15)', border: 'rgba(74,158,255,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(1, 30, '#4A9EFF')}{arm(64, 30, '#4A9EFF')}
        <rect x="11" y="11" width="58" height="58" rx="12" fill="#4A9EFF" />
        {face(40, 37, '#7dc0ff')}
      </svg>
    ),
  },
  {
    id: 'tri', name: 'Tri', color: '#FF7A2F', bg: 'rgba(255,122,47,0.15)', border: 'rgba(255,122,47,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 55, '#FF7A2F')}{arm(63, 55, '#FF7A2F')}
        <polygon points="40,5 76,74 4,74" fill="#FF7A2F" />
        {face(40, 50, '#ffb080')}
      </svg>
    ),
  },
  {
    id: 'rosie', name: 'Rosie', color: '#FF6B9D', bg: 'rgba(255,107,157,0.15)', border: 'rgba(255,107,157,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 45, '#FF6B9D')}{arm(63, 45, '#FF6B9D')}
        <circle cx="40" cy="44" r="32" fill="#FF6B9D" />
        {face(40, 42, '#ffaac8')}
      </svg>
    ),
  },
  {
    id: 'ovie', name: 'Ovie', color: '#4CAF50', bg: 'rgba(76,175,80,0.15)', border: 'rgba(76,175,80,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 43, '#4CAF50')}{arm(63, 43, '#4CAF50')}
        <ellipse cx="40" cy="46" rx="36" ry="24" fill="#4CAF50" />
        {face(40, 44, '#80c883')}
      </svg>
    ),
  },
  {
    id: 'hexy', name: 'Hexy', color: '#9B59B6', bg: 'rgba(155,89,182,0.15)', border: 'rgba(155,89,182,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(1, 36, '#9B59B6')}{arm(64, 36, '#9B59B6')}
        <polygon points="40,10 66,25 66,55 40,70 14,55 14,25" fill="#9B59B6" />
        {face(40, 38, '#c490d8')}
      </svg>
    ),
  },
  {
    id: 'gem', name: 'Gem', color: '#F5C518', bg: 'rgba(245,197,24,0.15)', border: 'rgba(245,197,24,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 38, '#F5C518')}{arm(63, 38, '#F5C518')}
        <polygon points="40,4 76,40 40,76 4,40" fill="#F5C518" />
        {face(40, 38, '#fbe080')}
      </svg>
    ),
  },
  {
    id: 'hearty', name: 'Hearty', color: '#E74C3C', bg: 'rgba(231,76,60,0.15)', border: 'rgba(231,76,60,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 41, '#E74C3C')}{arm(63, 41, '#E74C3C')}
        <path d="M40,68 C14,55 6,30 20,20 Q30,11 40,26 Q50,11 60,20 C74,30 66,55 40,68 Z" fill="#E74C3C" />
        {face(40, 38, '#f08080')}
      </svg>
    ),
  },
  {
    id: 'blobby', name: 'Blobby', color: '#1ABC9C', bg: 'rgba(26,188,156,0.15)', border: 'rgba(26,188,156,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 37, '#1ABC9C')}{arm(63, 37, '#1ABC9C')}
        <path d="M40,10 C56,8 72,16 74,32 C76,46 70,60 58,66 C50,70 34,72 22,64 C10,56 6,42 8,28 C10,16 24,12 40,10 Z" fill="#1ABC9C" />
        {face(40, 37, '#70d8c4')}
      </svg>
    ),
  },
  {
    id: 'stary', name: 'Stary', color: '#FF6B6B', bg: 'rgba(255,107,107,0.15)', border: 'rgba(255,107,107,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        <polygon points="40,7 48,29 71,30 53,44 59,67 40,54 21,67 27,44 9,30 32,29" fill="#FF6B6B" />
        {face(40, 37, '#ffaaaa')}
      </svg>
    ),
  },
  {
    id: 'penni', name: 'Penni', color: '#8BC34A', bg: 'rgba(139,195,74,0.15)', border: 'rgba(139,195,74,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 40, '#8BC34A')}{arm(63, 40, '#8BC34A')}
        <polygon points="40,8 70,31 58,66 22,66 10,31" fill="#8BC34A" />
        {face(40, 42, '#bcd98c')}
      </svg>
    ),
  },
  {
    id: 'sunny', name: 'Sunny', color: '#F39C12', bg: 'rgba(243,156,18,0.15)', border: 'rgba(243,156,18,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 39, '#F39C12')}{arm(63, 39, '#F39C12')}
        <path d="M8,56 Q8,10 40,10 Q72,10 72,56 L8,56 Z" fill="#F39C12" />
        {face(40, 36, '#f9c860')}
      </svg>
    ),
  },
  {
    id: 'cloudy', name: 'Cloudy', color: '#7986CB', bg: 'rgba(121,134,203,0.15)', border: 'rgba(121,134,203,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        <circle cx="40" cy="52" r="20" fill="#7986CB" />
        <circle cx="24" cy="42" r="16" fill="#7986CB" />
        <circle cx="56" cy="42" r="16" fill="#7986CB" />
        <circle cx="40" cy="32" r="16" fill="#7986CB" />
        <line x1="7" y1="50" x2="-3" y2="42" stroke="#7986CB" strokeWidth="9" strokeLinecap="round" />
        <line x1="73" y1="50" x2="83" y2="42" stroke="#7986CB" strokeWidth="9" strokeLinecap="round" />
        {face(40, 43, '#b0b8e8')}
      </svg>
    ),
  },
];

const MEET_CONFETTI = ['🎊', '✨', '🌟', '🎉', '⭐', '💫', '🎈', '🦋', '🌈', '💝', '🎀', '🎁'];

const stepVariants = {
  enter: { opacity: 0, x: 60 },
  center: { opacity: 1, x: 0, transition: { duration: 0.3, ease: 'easeOut' as const } },
  exit: { opacity: 0, x: -60, transition: { duration: 0.2, ease: 'easeIn' as const } },
};

// Static particle config generated once
const PARTICLES = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  x: (i * 37 + 11) % 100,
  y: (i * 53 + 7) % 100,
  size: 2 + (i % 3),
  duration: 4 + (i % 5) * 1.2,
  delay: (i * 0.4) % 3,
  opacity: 0.15 + (i % 4) * 0.08,
}));

function DarkBackground() {
  return (
    <div className="pointer-events-none fixed inset-0 z-0">
      {/* Base dark + grid */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: '#0D0F1C',
          backgroundImage: `
            linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />
      {/* Amber glow orb */}
      <div
        className="absolute"
        style={{
          top: '30%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245,158,11,0.12) 0%, rgba(139,92,246,0.06) 45%, transparent 70%)',
        }}
      />
      {/* Floating particles */}
      {PARTICLES.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            backgroundColor: p.id % 3 === 0 ? '#F59E0B' : p.id % 3 === 1 ? '#7C3AED' : '#ffffff',
            opacity: p.opacity,
          }}
          animate={{ y: [0, -14, 0], opacity: [p.opacity, p.opacity * 2.5, p.opacity] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const setProfile = useAuthStore((s) => s.setProfile);

  const [step, setStep] = useState(0);
  const [selectedTone, setSelectedTone] = useState<ToneOption>('cheerful');
  const [sidekickName, setSidekickName] = useState('KOKO');
  const [selectedAvatar, setSelectedAvatar] = useState(AVATARS[0].id);
  const [submitting, setSubmitting] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [error, setError] = useState('');

  const handleComplete = async () => {
    if (!profile || submitting) return;
    setSubmitting(true);
    useDarkStore.getState().resetPlan();

    try {
      const updateProfilePromise = updateProfile({
        profileId: profile.id,
        fields: {
          explorerName: profile.explorerName,
          sidekickPreferences: { name: sidekickName, tone: selectedTone, avatarId: selectedAvatar },
          hasCompletedSignUp: true,
        },
      });

      const needsActivation = !profile.subscriptionStatus || profile.subscriptionStatus === 'pending_consent';
      const completeSignUpPromise = needsActivation
        ? completeSignUp({ profileId: profile.id }).catch((err) => { console.error('completeSignUp failed:', err); })
        : Promise.resolve();

      await Promise.all([updateProfilePromise, completeSignUpPromise]);

      setProfile({
        ...profile,
        hasCompletedSignUp: true,
        sidekickPreferences: { name: sidekickName, tone: selectedTone, avatarId: selectedAvatar },
        subscriptionStatus: needsActivation ? 'active' : profile.subscriptionStatus,
      });

      setShowIntro(true);
      setTimeout(() => { navigate('/home', { replace: true }); }, 4000);
    } catch (err) {
      console.error('Onboarding error:', err);
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(message);
      setSubmitting(false);
      setStep(1);
    }
  };

  const handleMeetSidekick = () => {
    const name = sidekickName.trim();
    if (!name) return;
    const modResult = moderateText(name, true);
    if (!modResult.passed) {
      setError(modResult.message ?? 'Sidekick name not allowed.');
      return;
    }
    setError('');
    setStep(2);
    setTimeout(() => { handleComplete(); }, 3000);
  };

  if (showIntro) {
    return (
      <AnimatedPage>
        <div className="relative flex min-h-screen flex-col items-center justify-center px-4" style={{ backgroundColor: '#0D0F1C' }}>
          <DarkBackground />
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="relative z-10 text-center"
          >
            <div className="text-6xl">🚀</div>
            <h1 className="mt-4 font-marker text-3xl text-white">Welcome to Your Startup!</h1>
            <p className="mt-3 font-gill text-lg" style={{ color: 'rgba(232,234,240,0.7)' }}>
              You'll build your business in 6 stages.
            </p>
            <p className="mt-2 font-gill text-sm" style={{ color: 'rgba(232,234,240,0.35)' }}>Redirecting…</p>
          </motion.div>
        </div>
      </AnimatedPage>
    );
  }

  // Shared card style
  const card = {
    background: 'rgba(22,25,41,0.85)',
    border: '1px solid rgba(255,255,255,0.1)',
    backdropFilter: 'blur(12px)',
  };

  const inputCls =
    'mt-6 w-full rounded-xl px-4 py-3 text-center text-lg font-medium text-white placeholder-white/20 outline-none transition-colors focus:ring-2 focus:ring-amber-400/60';
  const inputStyle = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
  };

  const btnPrimary =
    'w-full rounded-xl py-3 font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed';
  const btnSecondary =
    'flex-1 rounded-xl py-3 font-semibold transition-colors';

  return (
    <AnimatedPage>
      <div className="relative flex min-h-screen flex-col" style={{ backgroundColor: '#0D0F1C' }}>
        <DarkBackground />
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-8">

        <div className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="relative z-10 w-full max-w-md">
          {/* Step indicator */}
          <div className="mb-8 flex justify-center gap-2">
            {[0, 1, 2].map((s) => (
              <div
                key={s}
                className="h-2 rounded-full transition-all duration-300"
                style={{
                  width: s === step ? '2rem' : '1.75rem',
                  background: s <= step ? '#F59E0B' : 'rgba(255,255,255,0.15)',
                }}
              />
            ))}
          </div>

          {/* Confetti on step 2 */}
          {step === 2 && (
            <div className="pointer-events-none fixed inset-0 z-10 overflow-hidden">
              {Array.from({ length: 24 }, (_, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 1, x: `${(i * 4.2) % 100}vw`, y: '-8vh', rotate: 0, scale: 0.8 + (i % 3) * 0.3 }}
                  animate={{ y: '108vh', rotate: (i % 2 === 0 ? 1 : -1) * (180 + (i * 37) % 360), opacity: [1, 1, 1, 0.5, 0] }}
                  transition={{ duration: 2.2 + (i % 4) * 0.55, delay: (i % 8) * 0.15, ease: 'easeIn', repeat: Infinity, repeatDelay: 0.6 }}
                  className="absolute text-2xl"
                >
                  {MEET_CONFETTI[i % MEET_CONFETTI.length]}
                </motion.span>
              ))}
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* Step 0: Sidekick tone */}
            {step === 0 && (
              <motion.div key="step-0" variants={stepVariants} initial="enter" animate="center" exit="exit"
                className="rounded-2xl p-8 shadow-2xl" style={card}
              >
                <h1 className="text-center font-marker text-2xl text-white">
                  How should your sidekick talk to you?
                </h1>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {TONE_OPTIONS.map((option) => (
                    <motion.button
                      key={option.tone}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelectedTone(option.tone)}
                      className="rounded-xl p-4 text-left transition-all"
                      style={
                        selectedTone === option.tone
                          ? { background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.5)' }
                          : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }
                      }
                    >
                      <span className="text-2xl">{option.emoji}</span>
                      <p className="mt-1 font-semibold text-white">{option.label}</p>
                      <p className="mt-1 font-gill text-sm" style={{ color: 'rgba(232,234,240,0.5)' }}>{option.sample}</p>
                    </motion.button>
                  ))}
                </div>
                <motion.button whileTap={{ scale: 0.97 }} onClick={() => setStep(1)}
                  className={`mt-6 ${btnPrimary}`}
                  style={{ background: '#F59E0B' }}>
                  Continue
                </motion.button>
              </motion.div>
            )}

            {/* Step 1: Sidekick name + avatar */}
            {step === 1 && (
              <motion.div key="step-1" variants={stepVariants} initial="enter" animate="center" exit="exit"
                className="rounded-2xl p-8 shadow-2xl" style={card}
              >
                <h1 className="text-center font-marker text-2xl text-white">
                  Give your sidekick a name!
                </h1>
                <input
                  type="text"
                  value={sidekickName}
                  onChange={(e) => { setSidekickName(e.target.value); setError(''); }}
                  placeholder="KOKO"
                  className={inputCls}
                  style={inputStyle}
                  maxLength={20}
                />
                <p className="mt-6 text-center font-gill text-sm" style={{ color: 'rgba(232,234,240,0.5)' }}>
                  Pick your sidekick avatar
                </p>
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {AVATARS.map((avatar) => {
                    const isSelected = selectedAvatar === avatar.id;
                    return (
                      <motion.button
                        key={avatar.id}
                        whileHover={{ scale: 1.06 }}
                        whileTap={{ scale: 0.88 }}
                        onClick={() => setSelectedAvatar(avatar.id)}
                        className="flex items-center justify-center rounded-xl transition-all"
                        style={
                          isSelected
                            ? { background: avatar.bg, border: `2px solid ${avatar.border}`, boxShadow: `0 0 14px ${avatar.border}`, padding: 6 }
                            : { background: 'rgba(255,255,255,0.04)', border: '2px solid rgba(255,255,255,0.08)', padding: 6 }
                        }
                      >
                        <div style={{ width: 46, height: 46 }}>{avatar.svg()}</div>
                      </motion.button>
                    );
                  })}
                </div>

                {/* Preview card */}
                {(() => {
                  const av = AVATARS.find((a) => a.id === selectedAvatar) ?? AVATARS[0];
                  return (
                    <div className="mt-6 flex items-center gap-4 rounded-xl p-4"
                      style={{ background: av.bg, border: `1px solid ${av.border}` }}>
                      <div style={{ width: 56, height: 56, flexShrink: 0 }}>{av.svg()}</div>
                      <div>
                        <p className="font-marker text-white">{sidekickName || 'KOKO'}</p>
                        <p className="font-gill text-sm" style={{ color: 'rgba(232,234,240,0.5)' }}>
                          {TONE_OPTIONS.find((t) => t.tone === selectedTone)?.sample}
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {error && (
                  <p className="mt-4 rounded-lg p-3 font-gill text-sm text-red-400"
                    style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
                    role="alert">
                    {error}
                  </p>
                )}

                <div className="mt-6 flex gap-3">
                  <button onClick={() => setStep(0)} className={btnSecondary}
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(232,234,240,0.7)' }}>
                    Back
                  </button>
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={handleMeetSidekick}
                    disabled={!sidekickName.trim()}
                    className="flex-1 rounded-xl py-3 font-semibold text-white disabled:opacity-40"
                    style={{ background: '#F59E0B' }}
                  >
                    Continue
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Meet your sidekick */}
            {step === 2 && (
              <motion.div key="step-2" variants={stepVariants} initial="enter" animate="center" exit="exit"
                className="flex flex-col items-center py-12"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.3 }}
                  style={{ width: 128, height: 128 }}
                >
                  {(AVATARS.find((a) => a.id === selectedAvatar) ?? AVATARS[0]).svg()}
                </motion.div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-6 font-marker text-3xl text-white"
                >
                  Meet {sidekickName || 'KOKO'}!
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9 }}
                  className="mt-2 font-gill"
                  style={{ color: 'rgba(232,234,240,0.5)' }}
                >
                  Your business sidekick is ready
                </motion.p>
                {submitting && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="mt-6">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-amber-400/30 border-t-amber-400" />
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        </div>
      </div>
      </div>
      <AppFooter />
    </AnimatedPage>
  );
}
